import React, { useCallback, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import { NavigateFunction } from "react-router";
import { createSSERagQuery, getQuizQuestion } from "src/api/chat";
import { BASE_URL } from "src/config";
import { formatMessage } from "src/helpers/format-message";
import { MarkdownStreamParser } from "src/helpers/markdown-parser";
import { Message, MessageSender, SourceInfo } from "./types";
import { stripCitationMarkers } from "./components/Citation";

interface UseMessageStreamOptions {
  api: AxiosInstance;
  orgSlug: string;
  subOrgSlug: string;
  quizId: string;
  messagesListRef: React.RefObject<HTMLDivElement>;
  beginAutoscroll: () => void;
  navigate: NavigateFunction;
  isMessageIncoming: boolean;
  setIsMessageIncoming: React.Dispatch<React.SetStateAction<boolean>>;
}

// Owns the conversation message state and the SSE streaming pipeline that
// feeds it. The hook exposes messages/setMessages so voice (useOpenAIRealtime)
// can share the same list, plus sendMessage and fetchNextQuizQuestion for the
// quiz flow. Direct port of the in-component sendMessage / fetchNextQuizQuestion
// including their inline [DONE] / [DOCS] / [IMAGES] / [ERROR] SSE parsing.
export const useMessageStream = ({
  api,
  orgSlug,
  subOrgSlug,
  quizId,
  messagesListRef,
  beginAutoscroll,
  navigate,
  isMessageIncoming,
  setIsMessageIncoming,
}: UseMessageStreamOptions) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const markdownParser = useRef(new MarkdownStreamParser());
  // Pre-existing behavior: ref captures the initial subOrgSlug only. Not
  // updated if the URL changes while this screen stays mounted.
  const suborgSlugRef = useRef(subOrgSlug || "");

  const fetchNextQuizQuestion = useCallback(async () => {
    try {
      const currentSubOrgSlug = suborgSlugRef.current;

      const response = await getQuizQuestion(
        api,
        currentSubOrgSlug.length > 0 ? currentSubOrgSlug : orgSlug,
        quizId || undefined,
      );

      // If response contains a new quiz_id, navigate to new URL with route params
      if (response && response.quiz_id && response.quiz_id !== quizId) {
        if (subOrgSlug) {
          navigate(`/${orgSlug}/quiz/${response.quiz_id}/${subOrgSlug}`, {
            replace: true,
          });
        } else {
          navigate(`/${orgSlug}/quiz/${response.quiz_id}`, {
            replace: true,
          });
        }
      }

      if (response && response.quiz_question) {
        return response.quiz_question;
      }

      return "No question found";
    } catch (error) {
      console.error("Error fetching next quiz question:", error);
      return "Error fetching next quiz question";
    }
  }, [api, orgSlug, quizId, navigate, subOrgSlug]);

  const sendMessage = useCallback(
    async (message: string, queryType: string) => {
      if (!message || message.trim() === "") {
        return;
      }

      const currentSubOrgSlug = suborgSlugRef.current;

      // For QUIZ query type, validate that the answer is a multiple choice format
      if (queryType === "QUIZ") {
        const isValidQuizAnswer = /^[A-Za-z](\.|:|\))?(\s.*)?$/.test(
          message.trim(),
        );

        if (!isValidQuizAnswer) {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: prevMessages.length + 1,
              message: message,
              sender: MessageSender.User,
              docs: [],
              imagesLoading: false,
            },
            {
              id: prevMessages.length + 2,
              message:
                "Please format your answer as a multiple choice option (A, B, C, etc.).",
              sender: MessageSender.Bot,
              isProcessing: false,
              docs: [],
              imagesLoading: false,
            },
          ]);

          beginAutoscroll();
          return;
        }
      }

      const useQdrant =
        new URLSearchParams(window.location.search).get("qdrant") === "1";
      const quizIdParam = queryType === "QUIZ" ? quizId : null;

      const response = await createSSERagQuery(
        api,
        currentSubOrgSlug.length > 0 ? currentSubOrgSlug : orgSlug,
        message,
        useQdrant,
        quizIdParam || undefined,
      );

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: prevMessages.length + 1,
          message: message,
          sender: MessageSender.User,
          docs: [],
          imagesLoading: false,
        },
        {
          id: prevMessages.length + 2,
          message: "",
          sender: MessageSender.Bot,
          isProcessing: true,
          docs: [],
          images: [],
          imagesLoading: false,
        },
      ]);

      beginAutoscroll();

      const eventSource = new EventSource(`${BASE_URL}${response.stream_url}`);

      let incomingMessage = "";
      let docs: string[] = [];
      let images: string[] = [];
      let isProcessing = true;

      setIsMessageIncoming(true);

      // Citations arrive on a dedicated "sources" SSE event type (the body is
      // a JSON map from marker to source metadata). The legacy [SOURCES]
      // data-prefix path on the default message channel is also accepted in
      // case the backend ever sends it that way. Each frame is merged into
      // the in-flight bot message so chips can render once the message text
      // is settled.
      const handleSourcesPayload = (raw: string) => {
        try {
          const newSources = JSON.parse(raw) as Record<string, SourceInfo>;
          setMessages((prevMessages) => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage.sender === "bot") {
              lastMessage.sources = {
                ...(lastMessage.sources || {}),
                ...newSources,
              };
            }
            return [...prevMessages];
          });
        } catch (e) {
          console.error("Failed to parse sources payload:", e);
        }
      };

      eventSource.addEventListener("message", (event) => {
        if (messagesListRef.current) {
          messagesListRef.current.scrollTo(
            0,
            messagesListRef.current.scrollHeight,
          );
        }

        if (event.data === "[DONE]") {
          // Process any remaining incomplete markdown
          const remainingText = markdownParser.current.getRemaining();
          if (remainingText) {
            incomingMessage += formatMessage(remainingText);

            setMessages((prevMessages) => {
              const lastMessage = prevMessages[prevMessages.length - 1];
              if (lastMessage.sender === "bot") {
                lastMessage.message = incomingMessage;
                lastMessage.docs = docs;
                lastMessage.images = images;
                if (images.length > 0) {
                  lastMessage.imagesLoading = false;
                }
              }
              return [...prevMessages];
            });
          }

          markdownParser.current = new MarkdownStreamParser();

          setMessages((prevMessages) => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage.sender === "bot") {
              lastMessage.message = incomingMessage;
              lastMessage.isProcessing = false;
              lastMessage.docs = docs;
            }
            return [...prevMessages];
          });

          setIsMessageIncoming(false);
          eventSource.close();

          if (messagesListRef.current) {
            messagesListRef.current.scrollTo(
              0,
              messagesListRef.current.scrollHeight,
            );
          }

          if (queryType === "QUIZ") {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                id: prevMessages.length + 1,
                message: "Processing...",
                sender: MessageSender.Bot,
                isProcessing: true,
                docs: [],
                imagesLoading: false,
              },
            ]);

            fetchNextQuizQuestion().then((nextQuestion) => {
              setMessages((prevMessages) => {
                const lastMessage = prevMessages[prevMessages.length - 1];
                if (lastMessage.sender === "bot") {
                  lastMessage.message = formatMessage(nextQuestion);
                  lastMessage.isProcessing = false;
                }
                return [...prevMessages];
              });

              beginAutoscroll();

              if (messagesListRef.current) {
                messagesListRef.current.scrollTo(
                  0,
                  messagesListRef.current.scrollHeight,
                );
              }
            });
          }
        } else if (event.data.startsWith("[IMAGES_LOADING]")) {
          setMessages((prevMessages) => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage.sender === "bot") {
              lastMessage.imagesLoading = true;
            }
            return [...prevMessages];
          });
        } else if (event.data.startsWith("[CANCEL_IMAGES]")) {
          setMessages((prevMessages) => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage.sender === "bot") {
              lastMessage.imagesLoading = false;
            }
            return [...prevMessages];
          });
        } else if (event.data.startsWith("[IMAGES]")) {
          const imageString = event.data.replace("[IMAGES]", "");
          images = imageString
            .split("@@@@")
            .filter((image: string) => image.trim() !== "");
        } else if (event.data.startsWith("[DOCS]")) {
          const docString = event.data.replace("[DOCS]", "");
          docs = docString
            .split("@@@@")
            .filter((doc: string) => doc.trim() !== "");
        } else if (event.data.startsWith("[SOURCES]")) {
          handleSourcesPayload(event.data.replace("[SOURCES]", ""));
        } else if (event.data.startsWith("[ERROR]")) {
          console.error("Error:", event.data);
          setIsMessageIncoming(false);
          eventSource.close();
        } else {
          if (isProcessing) {
            isProcessing = false;
            setMessages((prevMessages) => {
              const lastMessage = prevMessages[prevMessages.length - 1];
              if (lastMessage.sender === "bot") {
                lastMessage.isProcessing = false;
              }
              return [...prevMessages];
            });
          }

          const formattedChunk = markdownParser.current.processChunk(
            event.data,
          );

          if (formattedChunk) {
            incomingMessage += formattedChunk;

            if (latestMessageRef.current) {
              // Hide citation markers ([sop-xxx] etc.) during streaming;
              // they re-appear as numbered chips once isMessageIncoming
              // flips false and MessageBubble swaps to the React-tree
              // render. The accumulator keeps the markers so the final
              // state.message has them for chip placement.
              latestMessageRef.current.innerHTML =
                stripCitationMarkers(incomingMessage);
            }
          }

          if (isMessageIncoming && messagesListRef.current) {
            messagesListRef.current.scrollTo(
              0,
              messagesListRef.current.scrollHeight,
            );
          }
        }
      });

      eventSource.addEventListener("sources", (event) => {
        handleSourcesPayload((event as MessageEvent).data);
      });

      eventSource.addEventListener("error", (event) => {
        console.error("Error:", event);
        setIsMessageIncoming(false);
        eventSource.close();
      });

      eventSource.addEventListener("close", () => {
        setIsMessageIncoming(false);
      });
    },
    [
      api,
      orgSlug,
      beginAutoscroll,
      fetchNextQuizQuestion,
      isMessageIncoming,
      quizId,
      messagesListRef,
      setIsMessageIncoming,
    ],
  );

  return {
    messages,
    setMessages,
    sendMessage,
    fetchNextQuizQuestion,
    latestMessageRef,
  };
};
